import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";

const sections = [
  { id: "acceptance", title: "1. Acceptance of Terms" },
  { id: "who-we-are", title: "2. About Thorpe Dellow" },
  { id: "information-not-advice", title: "3. Information Only — Not Financial Advice" },
  { id: "eligibility", title: "4. Investor Eligibility" },
  { id: "risk-warnings", title: "5. Risk Warnings" },
  { id: "ip", title: "6. Intellectual Property" },
  { id: "prohibited", title: "7. Prohibited Uses" },
  { id: "third-party", title: "8. Third-Party Links" },
  { id: "liability", title: "9. Disclaimer & Limitation of Liability" },
  { id: "governing-law", title: "10. Governing Law" },
  { id: "changes", title: "11. Changes to These Terms" },
  { id: "contact", title: "12. Contact Us" },
];

interface SectionProps {
  id: string;
  title: string;
  children: React.ReactNode;
}

function Section({ id, title, children }: SectionProps) {
  return (
    <section id={id} className="scroll-mt-28 mb-12">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-1 h-6 rounded-full flex-shrink-0" style={{ background: "linear-gradient(to bottom, #C9A84C, #E2C27A)" }} />
        <h2 className="text-xl font-bold text-[#071A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>
          {title}
        </h2>
      </div>
      <div className="text-slate-600 text-sm leading-relaxed space-y-4 pl-4">
        {children}
      </div>
    </section>
  );
}

function Para({ children }: { children: React.ReactNode }) {
  return <p className="text-slate-600 text-sm leading-relaxed">{children}</p>;
}

function Ul({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2 mt-2">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-2.5 text-sm text-slate-600">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-checkbox-circle-line text-[#C9A84C] text-xs leading-none" />
          </div>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function WarningBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 rounded-xl px-5 py-4 my-4" style={{ background: "rgba(201,168,76,0.07)", border: "1px solid rgba(201,168,76,0.25)" }}>
      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
        <i className="ri-error-warning-line text-[#C9A84C] text-sm leading-none" />
      </div>
      <p className="text-sm text-[#071A2E] leading-relaxed">{children}</p>
    </div>
  );
}

export default function TermsOfUsePage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="min-h-screen bg-[#EEF2F9]">
      <Navbar />

      {/* Hero */}
      <section
        className="relative w-full overflow-hidden"
        style={{
          minHeight: 300,
          background: "linear-gradient(135deg, #071A2E 0%, #0D2B4A 60%, #071A2E 100%)",
        }}
      >
        <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: "linear-gradient(90deg, transparent, #C9A84C 30%, #E2C27A 50%, #C9A84C 70%, transparent)" }} />
        <div className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ background: "linear-gradient(to bottom, transparent, #C9A84C 25%, #E2C27A 55%, #C9A84C 80%, transparent)" }} />
        <div className="absolute top-[-60px] right-[-60px] w-80 h-80 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(201,168,76,0.08) 0%, transparent 70%)" }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 pt-36 pb-14">
          <div className="flex items-center gap-2 text-xs text-white/40 mb-6">
            <Link to="/" className="hover:text-white/70 transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line leading-none" />
            <span className="text-white/70">Terms of Use</span>
          </div>
          <span className="inline-flex items-center gap-2 bg-[#C9A84C]/15 border border-[#C9A84C]/30 text-[#C9A84C] text-[10px] font-bold uppercase tracking-[0.2em] px-4 py-1.5 rounded-full mb-5">
            <i className="ri-file-list-3-line text-xs leading-none" />
            Governed by English Law
          </span>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            Terms of Use
          </h1>
          <p className="text-white/55 text-sm max-w-xl leading-relaxed">
            Please read these Terms of Use carefully before accessing or using the Thorpe Dellow website. By using this website, you agree to be bound by these terms.
          </p>
          <p className="text-white/35 text-xs mt-4">Last updated: 28 March 2026 &nbsp;·&nbsp; Version 1.0</p>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-14">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-10">

          {/* Sidebar TOC */}
          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-28 bg-white rounded-2xl p-6 border border-slate-100">
              <p className="text-[10px] font-bold text-[#C9A84C] uppercase tracking-widest mb-4">Contents</p>
              <nav className="flex flex-col gap-1">
                {sections.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => scrollTo(s.id)}
                    className="text-left text-xs text-slate-500 hover:text-[#071A2E] py-1.5 px-2 rounded-lg hover:bg-[#EEF2F9] transition-colors cursor-pointer"
                  >
                    {s.title}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main content */}
          <div className="lg:col-span-3 bg-white rounded-2xl p-8 md:p-10">

            <Section id="acceptance" title="1. Acceptance of Terms">
              <Para>
                By accessing or using the Thorpe Dellow website at <strong className="text-[#071A2E]">thorpedellow.com</strong> (the &quot;Website&quot;), you agree to be bound by these Terms of Use (&quot;Terms&quot;). If you do not agree to these Terms, you must immediately cease using the Website.
              </Para>
              <Para>
                These Terms apply to all visitors, users, and investors who access or use the Website. Your continued use of the Website following the posting of any updates to these Terms constitutes your acceptance of those changes.
              </Para>
            </Section>

            <Section id="who-we-are" title="2. About Thorpe Dellow">
              <Para>
                Thorpe Property Development Ltd (Company No. 07590563), trading as Thorpe Dellow, is a specialist alternative investment firm registered in England and Wales. Our registered office is at One Embankment, Leeds LS1 4DW, United Kingdom.
              </Para>
              <Para>
                All financial promotions published on this Website are issued in compliance with Section 21 of the Financial Services and Markets Act 2000 (FSMA). This Website is directed solely at persons who qualify as Certified High Net Worth Individuals, Self-Certified Sophisticated Investors, or eligible retail investors as defined under applicable UK financial services legislation.
              </Para>
            </Section>

            <Section id="information-not-advice" title="3. Information Only — Not Financial Advice">
              <WarningBox>
                Nothing on this Website constitutes financial, investment, legal, or tax advice. All content is provided for informational purposes only. You should seek independent professional advice before making any investment decision.
              </WarningBox>
              <Para>
                The information on this Website, including any Information Memorandum, product summaries, or return illustrations, is provided on an &quot;as is&quot; basis. While we endeavour to keep the information accurate and up to date, we make no warranties or representations, express or implied, about the completeness, accuracy, reliability, or suitability of the information provided.
              </Para>
              <Para>
                Past performance is not a guide to future results. Target returns are illustrative only and are not guaranteed.
              </Para>
            </Section>

            <Section id="eligibility" title="4. Investor Eligibility">
              <Para>
                Access to detailed investment information, including our Information Memorandum, is restricted to qualifying investors only. By requesting access to restricted materials on this Website, you confirm that you are one of the following:
              </Para>
              <Ul items={[
                "A Certified High Net Worth Individual as defined in the Financial Services and Markets Act 2000 (Financial Promotion) Order 2005, Article 48",
                "A Self-Certified Sophisticated Investor as defined in the same Order, Article 50A",
                "An Eligible Retail Investor subject to an appropriateness assessment",
              ]} />
              <Para>
                If you do not qualify under any of the above categories, you must not access restricted investment materials on this Website. Thorpe Dellow reserves the right to withdraw access to any materials at any time without notice.
              </Para>
            </Section>

            <Section id="risk-warnings" title="5. Risk Warnings">
              <WarningBox>
                Investments in Thorpe Dellow products are speculative and involve a significant degree of risk. Investors may lose some or all of their capital.
              </WarningBox>
              <Para>Key risks include, but are not limited to:</Para>
              <Ul items={[
                "Capital is at risk — you may not get back the amount you invest",
                "Investments are illiquid — there is no readily available secondary market",
                "Target returns are not guaranteed and may not be achieved",
                "Investments are not protected by the Financial Services Compensation Scheme (FSCS)",
                "Past performance is not indicative of future results",
                "Tax treatment depends on your individual circumstances and may be subject to change",
              ]} />
              <Para>Please refer to our <Link to="/risk-disclaimer" className="text-[#C9A84C] hover:underline cursor-pointer">Risk Disclaimer</Link> for a full description of risks associated with our products.</Para>
            </Section>

            <Section id="ip" title="6. Intellectual Property">
              <Para>
                All content on this Website — including text, graphics, logos, images, data compilations, and software — is the property of Thorpe Dellow and is protected by UK and international copyright, trademark, and other intellectual property laws.
              </Para>
              <Para>You may:</Para>
              <Ul items={[
                "Print or download pages from the Website for your personal, non-commercial use",
                "Share links to pages on the Website",
              ]} />
              <Para>You must not:</Para>
              <Ul items={[
                "Reproduce, modify, distribute, or republish any content from this Website without our prior written consent",
                "Remove any copyright or proprietary notices from any content",
                "Use any content from this Website for any commercial purpose",
                "Frame or mirror any content of this Website on any other website",
              ]} />
            </Section>

            <Section id="prohibited" title="7. Prohibited Uses">
              <Para>In using this Website, you agree not to:</Para>
              <Ul items={[
                "Use the Website in any way that violates any applicable local, national, or international law or regulation",
                "Transmit any unsolicited or unauthorised advertising or promotional material",
                "Knowingly transmit any data, virus, or malicious code that may adversely affect the Website",
                "Attempt to gain unauthorised access to any part of the Website or its related infrastructure",
                "Use automated bots, scrapers, or crawlers to collect data from the Website without our express written consent",
                "Engage in any conduct that restricts or inhibits any other user from using or enjoying the Website",
              ]} />
            </Section>

            <Section id="third-party" title="8. Third-Party Links">
              <Para>
                This Website may contain links to third-party websites or resources. These links are provided for your convenience only. Thorpe Dellow has no control over the content of those sites or resources and accepts no responsibility for them or for any loss or damage that may arise from your use of them.
              </Para>
              <Para>
                The inclusion of a link does not imply endorsement by Thorpe Dellow of the linked website or its operators.
              </Para>
            </Section>

            <Section id="liability" title="9. Disclaimer & Limitation of Liability">
              <Para>
                To the fullest extent permitted by applicable law, Thorpe Dellow excludes all warranties, conditions, and representations in connection with this Website and its content.
              </Para>
              <Para>
                Thorpe Dellow will not be liable for any loss or damage of any kind — including direct, indirect, incidental, or consequential loss — arising from:
              </Para>
              <Ul items={[
                "Your reliance on information published on this Website",
                "Your inability to access or use the Website at any time",
                "Any errors, inaccuracies, or omissions in the Website content",
                "Unauthorised access to or alteration of your transmissions or data",
              ]} />
              <Para>
                Nothing in these Terms shall exclude or limit our liability for death or personal injury caused by negligence, fraud or fraudulent misrepresentation, or any other liability that cannot be excluded or limited by English law.
              </Para>
            </Section>

            <Section id="governing-law" title="10. Governing Law">
              <Para>
                These Terms of Use and any dispute or claim arising out of or in connection with them (including non-contractual disputes or claims) shall be governed by and construed in accordance with the <strong className="text-[#071A2E]">laws of England and Wales</strong>.
              </Para>
              <Para>
                The courts of England and Wales shall have exclusive jurisdiction to settle any dispute or claim arising out of or in connection with these Terms or their subject matter.
              </Para>
            </Section>

            <Section id="changes" title="11. Changes to These Terms">
              <Para>
                We may revise these Terms from time to time. Any changes will be posted on this page with an updated &quot;Last updated&quot; date. It is your responsibility to check this page periodically for updates.
              </Para>
              <Para>
                Your continued use of the Website following the posting of changes constitutes your acceptance of the revised Terms.
              </Para>
            </Section>

            <Section id="contact" title="12. Contact Us">
              <Para>If you have any questions about these Terms, please contact us:</Para>
              <div className="bg-[#071A2E] rounded-2xl p-6 mt-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {[
                    { icon: "ri-mail-line", label: "Email", value: "legal@thorpedellow.com", href: "mailto:legal@thorpedellow.com" },
                    { icon: "ri-map-pin-2-line", label: "Post", value: "One Embankment, Leeds LS1 4DW, United Kingdom" },
                  ].map((c) => (
                    <div key={c.label} className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#C9A84C]/15 border border-[#C9A84C]/25 flex-shrink-0">
                        <i className={`${c.icon} text-[#C9A84C] text-sm leading-none`} />
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-0.5">{c.label}</p>
                        {c.href ? (
                          <a href={c.href} className="text-white text-xs font-medium hover:text-[#C9A84C] transition-colors">{c.value}</a>
                        ) : (
                          <p className="text-white text-xs font-medium">{c.value}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Section>

            {/* Bottom nav */}
            <div className="border-t border-slate-100 pt-8 flex flex-col sm:flex-row gap-3 justify-between items-center">
              <p className="text-xs text-slate-400">Last updated: 28 March 2026</p>
              <div className="flex gap-3">
                <Link to="/privacy-policy" className="text-xs text-[#C9A84C] hover:underline cursor-pointer whitespace-nowrap">Privacy Policy</Link>
                <span className="text-slate-300">·</span>
                <Link to="/risk-disclaimer" className="text-xs text-[#C9A84C] hover:underline cursor-pointer whitespace-nowrap">Risk Disclaimer</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
